import { Component, OnInit, ViewChild } from '@angular/core';
import { TrainingStatusConstants } from '../constants/training-status-constants';
import { MessageService } from 'primeng/api';
import { TrainingStatusService } from '../services/training-status.service';
import { FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import { CommonService } from '../../shared/services/common.service';
import { TrainingStatusItem } from '../models/TrainingStatusItem';
import { IEQMSScopeLookUpDetails } from '../../shared/models/scopes/eqms-Scope-lookup-details';
import { EqmsScopeManagementService } from '../../shared/services/eqms-scope-management.service';
import { LabelConstants } from '../../shared/constants/label-constants';
import { FilterAndSortDetails } from '../../shared/models/filter-sort-details';
import { PanelHubComponent } from '../../shared-components/panel-hub/panel-hub.component';
import { PanelHubConfiguration } from '../../shared-components/panel-hub/models/panel-hub-configuration';
import { PaginationState } from '../../shared-components/panel-hub/models/pagination-state';
import { AppliedFilters } from '../../shared-components/panel-hub/models/applied-filters';
import { SortCriteria } from '../../shared-components/panel-hub/models/sort-criteria';
import { FilterConfig } from '../../shared-components/panel-hub/models/filter-config';
import { SortField } from '../../shared-components/panel-hub/models/sort-field';
import { Features } from '../../shared-components/panel-hub/enums/features.enum';
import { PaginationConfigByFeature } from '../../shared-components/panel-hub/constants/feature-pagination-configuration';
import { PanelHubConstants } from '../../shared-components/panel-hub/constants/panel-hub-constants';
import { PanelHubStateChangedEvent } from '../../shared-components/panel-hub/models/panel-hub-state-changed-event';
import { PanelHubEventType } from '../../shared-components/panel-hub/enums/panel-hub-eventtype';
import { TrainingStatusQueryResult } from '../models/training-status-query-result';
import { TrainingStatusReportSearchCriteria } from '../models/training-status-search-criteria';
import { TrainingStatusReportLookupDetails } from '../models/training-status-report-lookup';
import { NameValue } from '../../shared/models/name-value';

@Component({
  selector: 'app-training-status',
  templateUrl: './training-status.component.html',
  styleUrls: ['./training-status.component.scss'],
})
export class TrainingStatusComponent implements OnInit {
  topBarTitle: string = TrainingStatusConstants.TrainingStatusReport_Header;
  labelField: string = TrainingStatusConstants.labelField_Name;
  valueField: string = TrainingStatusConstants.labelField_Value;
  filterLength: number = TrainingStatusConstants.FilterLength;
  accessToTrainingStatusReport: boolean = false;
  TrainingStatusReportErrorMessage!: string;
  eQMSScopeList: any = [];
  errorMessages: any = [];
  trainingStatusForm!: FormGroup;
  mandatoryFields = this.getDefaultMandatoryFieldsObject();
  eQMSScopeSelected: any;
  showLoader : boolean = false;
  noData : boolean = false;
  isExportButtonAvailable: boolean = true;
  SpinnerMessage:string = TrainingStatusConstants.SpinnerMessageFor_TrainingStatusReport;
  displayAppPanel: boolean = false;
  
  @ViewChild(PanelHubComponent) 
  private panelHubComponent! : PanelHubComponent; 

  isTrainingStatusReportAvailable : boolean = false;
  hasUserSearched: boolean = false;
  panelHubConfig!: PanelHubConfiguration;
  pagedData: TrainingStatusQueryResult = { data: [], totalRecords: 0, pageNumber: 1, pageSize: 25 };
  currentPagination: PaginationState | null = null;
  currentFilters: AppliedFilters | null = null;
  currentSort: SortCriteria | null = null;
  lookupData: TrainingStatusReportLookupDetails | null = null;

  constructor(
    private trainingStatusReportService: TrainingStatusService,
    private formBuilder: FormBuilder, private eqmsScopeManagementService: EqmsScopeManagementService,
    private messageService: MessageService, private commonService: CommonService,
  ) {}

  ngOnInit(): void {
    this.trainingStatusForm = this.createFormGroupWithBuilderAndModel();
    this.getLookupData();
    this.initializePanelHubConfig();
  }

  private initializePanelHubConfig(): void {
    this.panelHubConfig = {
      feature: Features.TrainingStatusReportView,
      standardFilterCount: TrainingStatusConstants.FilterLength,
      enableSelection: false,
      enableToggling: false,
      showSelectAll: false,
      enableSelectAll: false,
      enableExport: true,
      filterConfigs: this.getFilterConfigurations(),
      sortOptions: this.getSortOptions(),
      paginationConfig: PaginationConfigByFeature[Features.TrainingStatusReportView]
    };
  }

  private getFilterConfigurations(): FilterConfig[] {
    if (!this.lookupData) {
      return [];
    }

    return [
      { 
        displayName: 'User', 
        fieldName: 'user', 
        options: this.lookupData.users.map(user => ({ 
          name: user.name, 
          value: user.code 
        }))
      },
      { 
        displayName: 'Type', 
        fieldName: 'type', 
        options: this.lookupData.types.map(type => ({ 
          name: type.name, 
          value: type.name 
        }))
      },
      { 
        displayName: 'Training Status', 
        fieldName: 'trainingStatus', 
        options: this.lookupData.trainingStatuses.map(status => ({ 
          name: status.name, 
          value: status.name 
        }))
      },
      { 
        displayName: 'Core/Elective', 
        fieldName: 'coreOrElective', 
        options: this.lookupData.coreOrElective.map(item => ({ 
          name: item.name, 
          value: item.name 
        }))
      },
      { 
        displayName: 'Curriculum', 
        fieldName: 'curriculum', 
        options: this.lookupData.curriculums.map(curriculum => ({ 
          name: curriculum.name, 
          value: curriculum.value.toString() 
        }))
      },
      { 
        displayName: 'Training Item Group', 
        fieldName: 'trainingItemGroups', 
        options: this.lookupData.trainingItemGroups.map(tig => ({ 
          name: tig.name, 
          value: tig.code 
        }))
      },
    ];
  }

  private getSortOptions(): SortField[] {
    return [
      { fieldDisplayName: 'User', fieldName: 'user' },
      { fieldDisplayName: 'Type', fieldName: 'type' },
      { fieldDisplayName: 'Training Status', fieldName: 'trainingStatus' },
      { fieldDisplayName: 'Core/Elective', fieldName: 'coreOrElective' },
      { fieldDisplayName: 'Curriculum', fieldName: 'curriculum' },
      { fieldDisplayName: 'Training Item Group', fieldName: 'trainingItemGroups' },
      { fieldDisplayName: 'Manager', fieldName: 'manager' },
      { fieldDisplayName: 'Cost Center', fieldName: 'costCenter' },
      { fieldDisplayName: 'Assignment Date', fieldName: 'assignmentDate' },
      { fieldDisplayName: 'Due Date', fieldName: 'dueDate' },
      { fieldDisplayName: 'Completed Date', fieldName: 'completedDate' },
      { fieldDisplayName: 'Expiration Date', fieldName: 'expirationDate' },
      { fieldDisplayName: 'Removal Date', fieldName: 'removalDate' },
      { fieldDisplayName: 'Cancelation Date', fieldName: 'cancelationDate' },
      { fieldDisplayName: 'Revocation Date', fieldName: 'revocationDate' }
    ];
  }

  createFormGroupWithBuilderAndModel() {
    return this.formBuilder.group({
      eQMSScopes: new FormControl(null, [Validators.required]),
      assignmentDateRange: new FormControl(null, [Validators.required]),
      trainingStatus: new FormControl(null), // Optional dropdown for training status
    });
  }

  getLookupData = () => {
    this.trainingStatusReportService.getLookupData().subscribe(
      {
        next: (lookupData) => {
          this.accessToTrainingStatusReport = true;
          this.lookupData = lookupData;
          this.populateEQMSScopeList(lookupData.scopes);
          this.initializePanelHubConfig(); // Re-initialize with lookup data
        },
        error: (err) => {
          this.accessToTrainingStatusReport = false;
          if(err.status === LabelConstants.Forbidden_StatusCode)
            this.TrainingStatusReportErrorMessage = err.error.detail;
        }
      });
  };

  private populateEQMSScopeList(locationsData: NameValue[]): void {
    for (let dataItem of locationsData) {
      this.eQMSScopeList.push({
        value: dataItem.value,
        name: dataItem.name,
      });
    }
  }

  private getDefaultMandatoryFieldsObject() {
    return {
      eQMSScopes: false,
      assignmentDateRange: false,
    };
  }

  getTrainingStatusReport() {
    this.validateTrainingStatusReportForm();
    if (this.errorMessages.length == 0) {
      this.hasUserSearched = true;
      
      // Reset Panel Hub component to clear filters and reset pagination
      if (this.panelHubComponent) {
        this.panelHubComponent.resetPanelConfigurations();
      }
      
      // Reset state - Panel Hub will use default pagination from config
      this.currentFilters = null;
      this.currentSort = null;
      this.currentPagination = null;
      
      // Reset pagedData to force paginator UI to show default values
      const defaultPageSize = this.panelHubConfig?.paginationConfig?.defaultPageSize || 25;
      this.pagedData = {
        data: [],
        totalRecords: 0,
        pageNumber: 1,
        pageSize: defaultPageSize
      };
      
      this.resetPaginationAndLoad();
    } else {
      // Construct error message
      let errorMsg = '';
      this.errorMessages.forEach((value: any, index: number) => {
        if (index != 0) errorMsg += ', ';
        errorMsg += value;
      });

      // method call to trigger toaster message
      this.toasterMessage(TrainingStatusConstants.Severity_Error, errorMsg);
    }
  }

  private resetPaginationAndLoad(): void {
    // Reset pagination by calling loadTrainingStatusData with null pagination
    // This will use default pagination from the configuration
    this.loadTrainingStatusData(null, null, null);
  }

  private loadTrainingStatusData(
    pagination: PaginationState | null,
    filters: AppliedFilters | null,
    sort: SortCriteria | null
  ): void {
    if (!this.hasUserSearched) {
      return;
    }

    const request: TrainingStatusReportSearchCriteria = {
      eQMSScope: this.trainingStatusForm?.getRawValue().eQMSScopes,
      assignmentDateRange: [
        this.trainingStatusForm?.getRawValue().assignmentDateRange[0].toDateString(),
        this.trainingStatusForm?.getRawValue().assignmentDateRange[1].toDateString()
      ]
    };

    // Handle training status from main dropdown
    const mainTrainingStatus = this.trainingStatusForm?.getRawValue().trainingStatus;
    
    // Merge main dropdown training status with panel hub filters
    let mergedFilters = filters ? { ...filters } : { filters: [] };
    
    if (mainTrainingStatus && mainTrainingStatus.length > 0) {
      // Check if panel hub already has training status filter
      const panelTrainingStatusFilter = mergedFilters.filters?.find(f => f.field === 'trainingStatus');
      
      if (panelTrainingStatusFilter && panelTrainingStatusFilter.values && panelTrainingStatusFilter.values.length > 0) {
        // Both main dropdown and panel filter have values - find intersection
        const intersection = mainTrainingStatus.filter((value: string) => 
          panelTrainingStatusFilter.values.includes(value)
        );
        
        // Early return if no intersection (AND condition results in empty set)
        if (intersection.length === 0) {
          this.pagedData = {
            data: [],
            totalRecords: 0,
            pageNumber: 1,
            pageSize: pagination?.pageSize || 25
          };
          this.noData = true;
          this.isTrainingStatusReportAvailable = true;
          this.displayAppPanel = true;
          this.showLoader = false;
          return;
        }
        
        // Update filter with intersection
        panelTrainingStatusFilter.values = intersection;
      } else {
        // Only main dropdown has values - add to filters
        if (!mergedFilters.filters) {
          mergedFilters.filters = [];
        }
        mergedFilters.filters.push({
          field: 'trainingStatus',
          values: mainTrainingStatus
        });
      }
    }

    this.showLoader = true;
    this.noData = false;

    this.trainingStatusReportService
      .getTrainingStatusReport(request, pagination, mergedFilters, sort)
      .subscribe({
        next: (result: TrainingStatusQueryResult) => {
          this.pagedData = {
            data: result.data,
            totalRecords: result.totalRecords,
            pageNumber: result.pageNumber,
            pageSize: result.pageSize
          };
          this.noData = this.commonService.IsListEmpty(result.data);
          this.isTrainingStatusReportAvailable = true;
          this.displayAppPanel = true;
          this.showLoader = false;
        },
        error: (err) => {
          console.error('Error while fetching Training Status Report', err);
          this.showLoader = false;
          this.noData = true;
        }
      });
  }

  onPanelHubStateChanged(event: PanelHubStateChangedEvent): void {
    // Only handle specific event types to avoid duplicate API calls
    switch (event.type) {
      case PanelHubEventType.FilterChanged:
        this.currentFilters = event.state.filters ?? null;
        // Reset to first page when filters change
        if (this.currentPagination) {
          this.currentPagination.pageNumber = 1;
        }
        // Clear data but keep pagination UI intact
        this.pagedData = {
          ...this.pagedData,
          data: []
        };
        this.loadTrainingStatusData(
          this.currentPagination,
          this.currentFilters,
          this.currentSort
        );
        break;

      case PanelHubEventType.SortChanged:
        this.currentSort = event.state.sort ?? null;
        // Clear data but keep pagination UI intact
        this.pagedData = {
          ...this.pagedData,
          data: []
        };
        this.loadTrainingStatusData(
          this.currentPagination,
          this.currentFilters,
          this.currentSort
        );
        break;

      case PanelHubEventType.PaginationChanged:
        this.currentPagination = event.state.pagination ?? null;
        // Clear data but keep pagination UI intact
        this.pagedData = {
          ...this.pagedData,
          data: []
        };
        this.loadTrainingStatusData(
          this.currentPagination,
          this.currentFilters,
          this.currentSort
        );
        break;

      // Ignore other event types
      default:
        break;
    }
  }

  onPanelEventHubInitialized(): void {
    // Panel hub initialized - can perform additional setup if needed
  }

  private validateTrainingStatusReportForm() {
    this.errorMessages = [];
    this.mandatoryFields = this.getDefaultMandatoryFieldsObject();
    if (this.trainingStatusForm.invalid) {
      Object.keys(this.trainingStatusForm.controls).forEach((key) => {
        if (this.trainingStatusForm.get(key)?.errors) {
          let errorText = this.getErrorMessageText(key);
          if (errorText != '') this.errorMessages.push(errorText);
        }
      });
    }
    this.validateAssignmentDateRange();
  }

  private getErrorMessageText(labelName: string) {
    let errorText: string = '';
    switch (labelName) {
      case 'eQMSScopes':
        errorText =
          TrainingStatusConstants.TrainingStatusReport_eQMSScope_Required;
        this.mandatoryFields.eQMSScopes = true;
        break;
      case 'assignmentDateRange':
        errorText =
          TrainingStatusConstants.TrainingStatusReport_AssignmentDateRange_Required;
        this.mandatoryFields.assignmentDateRange = true;
        break;
    }
    return errorText;
  }

  // Method to display toaster message
  toasterMessage(severity: string, summary: string) {
    this.messageService.add({
      severity: severity,
      summary: `${summary}`,
    });
  }

  validateAssignmentDateRange() {
    let assignmentDateRange =
      this.trainingStatusForm?.getRawValue().assignmentDateRange;
    if (assignmentDateRange != null && assignmentDateRange.length == 2) {
      if (assignmentDateRange[0] != null && assignmentDateRange[1] != null) {
        if (
          this.isRangeWithinOneYear(
            new Date(assignmentDateRange[0]),
            new Date(assignmentDateRange[1])
          ) == false
        ) {
          this.errorMessages.push(
            TrainingStatusConstants.TrainingStatusReport_RangeSelection_Error
          );
        }
      } else if (
        (assignmentDateRange[0] != null && assignmentDateRange[1] == null) ||
        (assignmentDateRange[0] == null && assignmentDateRange[1] != null)
      ) {
        this.errorMessages.push(
          TrainingStatusConstants.InvalidAssignmentDateRangeSelection
        );
      }
    }
  }

  // Assignment Date Range check - logic
  isRangeWithinOneYear(date1: Date, date2: Date): boolean {
    const oneYearInMillis = 365 * 24 * 60 * 60 * 1000; // milliseconds in a year
    const diff = Math.abs(date1.getTime() - date2.getTime());
    return diff <= oneYearInMillis;
  }

  openCourse(courseNumber : string){
    var url = location.origin.concat("/training/eqms.courses/", courseNumber);
    window.open(url, "_blank");
  }

  openDocument(documentNumber: string, version : string){
    var url = location.origin.concat("/eqms.documentDetails/", documentNumber, ":", version);
    window.open(url, "_blank");
  }



  TrainingStatusReportAccessErrorMessage() : string{
    if (!this.accessToTrainingStatusReport) {
      return this.TrainingStatusReportErrorMessage;
    }
    return TrainingStatusConstants.TrainingStatusReport_RoleAcessPermission_Fail;
  }

  calculateStatusColor = (status: string) : string => {
    switch (status) {
      case 'Pending':
        return 'status-orange';
      case 'Complete':
        return 'status-green';
      case 'Active':
        return 'status-green';
      case 'Cancelled':
      case 'Removed':
      case 'Revoked':
      case 'N/A':
        return 'status-gray';
      case 'Locked':
        return 'status-red';
      default:
        return '';
    }
  }

  exportExcel = () => {
    if (!this.hasUserSearched) {
      return;
    }

    // Prepare request with form values
    const request: TrainingStatusReportSearchCriteria = {
      eQMSScope: this.trainingStatusForm?.getRawValue().eQMSScopes,
      assignmentDateRange: [
        this.trainingStatusForm?.getRawValue().assignmentDateRange[0].toDateString(),
        this.trainingStatusForm?.getRawValue().assignmentDateRange[1].toDateString()
      ]
    };

    // Merge main dropdown training status with panel hub filters (same logic as loadTrainingStatusData)
    const mainTrainingStatus = this.trainingStatusForm?.getRawValue().trainingStatus;
    let mergedFilters = this.currentFilters ? { ...this.currentFilters } : { filters: [] };
    
    if (mainTrainingStatus && mainTrainingStatus.length > 0) {
      const panelTrainingStatusFilter = mergedFilters.filters?.find(f => f.field === 'trainingStatus');
      
      if (panelTrainingStatusFilter && panelTrainingStatusFilter.values && panelTrainingStatusFilter.values.length > 0) {
        const intersection = mainTrainingStatus.filter((value: string) => 
          panelTrainingStatusFilter.values.includes(value)
        );
        
        // Early return if no intersection
        if (intersection.length === 0) {
          this.showLoader = false;
          this.messageService.add({
            severity: TrainingStatusConstants.Severity_Error,
            summary: TrainingStatusConstants.ErrorMessage_ForNoTrainingOccurence,
          });
          return;
        }
        
        panelTrainingStatusFilter.values = intersection;
      } else {
        if (!mergedFilters.filters) {
          mergedFilters.filters = [];
        }
        mergedFilters.filters.push({
          field: 'trainingStatus',
          values: mainTrainingStatus
        });
      }
    }

    // Use current Panel Hub state (filters, sort, pagination) for export
    this.showLoader = true;
    this.trainingStatusReportService.exportTrainingStatusReport(
      request,
      this.currentPagination,
      mergedFilters,
      this.currentSort
    ).subscribe({
      next: (byteData: any) => {
        this.showLoader = false;
        
        // Exactly same pattern as Document export - decode base64 string
        if (byteData) {
          const byteCharacters = atob(byteData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          
          // Generate filename with eQMS scope and date range
          const scopeName = this.eQMSScopeList.find((data:any) => data.value === request.eQMSScope)?.name || 'Unknown';
          const startDate = this.date_TO_String(new Date(request.assignmentDateRange[0]));
          const endDate = this.date_TO_String(new Date(request.assignmentDateRange[1]));
          const fileName = `TrainingStatusReport_${scopeName}_${startDate}_${endDate}.xlsx`;
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.download = fileName;
          anchor.href = url;
          anchor.click();
          window.URL.revokeObjectURL(url);
        } else {
          this.messageService.add({
            severity: TrainingStatusConstants.Severity_Error,
            summary: TrainingStatusConstants.ErrorMessage_ForNoTrainingOccurence,
          });
        }
      },
      error: (error) => {
        this.showLoader = false;
        this.messageService.add({
          severity: TrainingStatusConstants.Severity_Error,
          summary: 'Export failed',
          detail: 'An error occurred while exporting the report.'
        });
      }
    });
  }

  private generateExcelData(filters: any){
    // Prepare request for Export
    const request: TrainingStatusReportSearchCriteria = {
      eQMSScope: this.trainingStatusForm?.getRawValue().eQMSScopes,
      assignmentDateRange: [
        this.trainingStatusForm?.getRawValue().assignmentDateRange[0].toDateString(),
        this.trainingStatusForm?.getRawValue().assignmentDateRange[1].toDateString()
      ]
    };
    var selectedeQMSScopeValue = this.eQMSScopeList.find((data:any) => data.value === request.eQMSScope)?.name;
    if (selectedeQMSScopeValue) {
      this.trainingStatusReportService.exportToExcel(filters, request, selectedeQMSScopeValue);
    }
  }

  searchAndExportExcel() {
    this.validateTrainingStatusReportForm();
    if (this.errorMessages.length == 0) {
      // Use the same export logic as the panel hub export button
      this.exportExcel();
    } else {
      // Construct error message
      let errorMsg = '';
      this.errorMessages.forEach((value: any, index: number) => {
        if (index != 0) errorMsg += ', ';
        errorMsg += value;
      });

      // method call to trigger toaster message
      this.toasterMessage(TrainingStatusConstants.Severity_Error, errorMsg);
    }
  }

  getDefaultFilterAndSortDetails = () : FilterAndSortDetails => {
    return {
      searchText: '',
      filterDetails:[],
      sortDetails:[],
      puid:null,
      isShowAll :false
    }
  }

  private date_TO_String(date_Object: Date): string {
    // get the year, month, date, hours, and minutes separately and append to the string.
    let date_String: string =
      date_Object.getFullYear() +
      "_" +
      (date_Object.getMonth() + 1) +
      "_" +
      date_Object.getDate();
    return date_String;
  }
}